import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import ShopNav from "@/components/ShopNav";
import Footer from "@/components/sections/Footer";
import { useLang } from "@/i18n/LanguageContext";
import { useCart } from "@/context/CartContext";
import { Bell, Check, Leaf } from "lucide-react";
import Seo, { websiteLd, breadcrumbLd, getSiteUrl } from "@/components/Seo";
import { PRE_HARVEST_MODE } from "@/lib/siteMode";
import WaitlistForm from "@/components/WaitlistForm";

const SITE_URL = getSiteUrl();
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StockBadge({ stock, t }) {
  if (stock <= 0)
    return (
      <span className="inline-flex items-center px-2.5 py-1 text-[10px] uppercase tracking-[0.24em] bg-charcoal text-cream-100">
        {t("shop_page.out_of_stock")}
      </span>
    );
  if (stock <= 12)
    return (
      <span className="inline-flex items-center px-2.5 py-1 text-[10px] uppercase tracking-[0.24em] bg-gold text-olive-900">
        {t("shop_page.low_stock")}
      </span>
    );
  return null;
}

export default function ShopPage({ forceLang }) {
  const { t, lang, setLang } = useLang();
  useEffect(() => {
    if (forceLang && forceLang !== lang) setLang(forceLang);
  }, [forceLang, lang, setLang]);
  const effectiveLang = forceLang || lang;
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [added, setAdded] = useState(null);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistProduct, setWaitlistProduct] = useState("");

  function openWaitlist(slug = "") {
    setWaitlistProduct(slug);
    setShowWaitlist(true);
  }

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((res) => setProducts(res.data))
      .catch(() => setError(t("common.error_generic")))
      .finally(() => setLoading(false));
  }, [t]);

  const handleAdd = (p) => {
    if (p.stock <= 0 || PRE_HARVEST_MODE) return;
    addItem(p, 1);
    setAdded(p.id);
    setTimeout(() => setAdded(null), 1400);
  };

  const isEs = effectiveLang === "es";
  const canonical = `${SITE_URL}${isEs ? "/tienda" : "/shop"}`;
  const alternates = {
    es: `${SITE_URL}/tienda`,
    en: `${SITE_URL}/shop`,
    "x-default": `${SITE_URL}/tienda`,
  };
  const productLinkBase = isEs ? "/producto/" : "/product/";

  // ItemList of currently visible SKUs. `listed !== false` mirrors the
  // sitemap/backend filter — hidden bundle variants (created by the wooden-
  // box selector) never leak into structured data.
  const visibleProducts = (products || []).filter(
    (p) => p.active !== false && p.listed !== false
  );
  const itemListLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: visibleProducts.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE_URL}${productLinkBase}${p.slug}`,
      name: isEs ? p.name_es : p.name_en,
    })),
  };
  const jsonLd = [
    websiteLd(),
    breadcrumbLd([
      { name: isEs ? "Inicio" : "Home", url: `${SITE_URL}${isEs ? "/" : "/en"}` },
      { name: isEs ? "Tienda" : "Shop", url: canonical },
    ]),
    itemListLd,
  ];

  return (
    <main data-testid="shop-page" className="min-h-screen bg-cream-100 text-charcoal">
      <Seo
        title={
          isEs
            ? "Tienda · AOVE de cosecha temprana | Cortijo Santa María Granada"
            : "Shop · Early-harvest EVOO | Cortijo Santa María Granada"
        }
        description={
          isEs
            ? PRE_HARVEST_MODE
              ? "AOVE premium de cosecha temprana de Cortijo Santa María. Próxima cosecha en preparación. Únete a la lista de espera y te avisaremos cuando esté disponible."
              : "Compra online el AOVE premium de Cortijo Santa María. Edición limitada de cosecha temprana de Limones, Moclín (Granada). Botella 500 ml y estuche de regalo con caja de madera con motivo del cortijo."
            : PRE_HARVEST_MODE
              ? "Premium early-harvest EVOO from Cortijo Santa María. Next harvest in preparation. Join the waitlist and we will notify you when it is available."
              : "Buy Cortijo Santa María premium EVOO online. Limited early harvest from Limones, Moclín (Granada). 500 ml bottle and wooden presentation gift box."
        }
        canonical={canonical}
        alternates={alternates}
        lang={effectiveLang}
        jsonLd={jsonLd}
      />
      <ShopNav />

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-16 sm:pt-24 pb-8">
        <span className="text-[11px] uppercase tracking-[0.32em] text-gold-deep font-medium">
          {t("shop_page.overline")}
        </span>
        <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] text-olive-900">
          {t("shop_page.title")}
        </h1>
        <p className="mt-5 max-w-xl text-olive-800/80 font-light text-lg">{t("shop_page.subtitle")}</p>

        <aside
          data-testid="shop-availability-banner"
          role="note"
          className="mt-10 flex items-start gap-4 border-l-2 border-gold-deep bg-cream-200/80 px-5 sm:px-7 py-5 sm:py-6"
        >
          <Leaf size={18} strokeWidth={1.3} className="text-gold-deep mt-0.5 shrink-0" />
          <p className="text-sm sm:text-base text-olive-900/85 font-light leading-relaxed max-w-2xl">
            {t("shop_page.availability_banner")}
          </p>
        </aside>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24">
        {loading && <div className="text-olive-800/60 font-light">{t("common.loading")}</div>}
        {error && <div className="text-red-700">{error}</div>}
        {!loading && !error && (
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 mt-8">
            {products.map((p) => {
              const name = isEs ? p.name_es : p.name_en;
              const desc = isEs ? p.short_desc_es : p.short_desc_en;
              const badge = isEs ? p.badge_es : p.badge_en;
              const savings = isEs ? p.savings_label_es : p.savings_label_en;
              const featured = !!p.featured;
              const soldOut = p.stock <= 0;
              return (
                <article
                  key={p.id}
                  data-testid={`shop-product-${p.slug}`}
                  className={[
                    "group flex flex-col transition-colors",
                    featured
                      ? "bg-cream-100 border-2 border-olive-700 shadow-[0_18px_40px_-22px_rgba(60,75,30,0.35)]"
                      : "bg-cream-100 border border-cream-400 hover:border-olive-700/40",
                  ].join(" ")}
                >
                  <Link to={`${productLinkBase}${p.slug}`} className="block relative aspect-[4/5] bg-cream-200 overflow-hidden">
                    <img
                      src={p.image_url}
                      alt={name}
                      loading="lazy"
                      className="h-full w-full object-contain p-10 sm:p-14 group-hover:scale-[1.02] transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4 flex flex-col gap-2">
                      <StockBadge stock={p.stock} t={t} />
                    </div>
                    {badge && (
                      <div
                        data-testid={`shop-badge-${p.slug}`}
                        className={[
                          "absolute top-4 right-4 inline-flex items-center text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 font-medium",
                          featured
                            ? "bg-olive-900 text-cream-100"
                            : "bg-cream-100/95 text-olive-900 border border-olive-700/30",
                        ].join(" ")}
                      >
                        {badge}
                      </div>
                    )}
                  </Link>
                  <div className="p-6 sm:p-8 flex flex-col flex-1">
                    <h2 className="font-serif text-2xl sm:text-3xl text-olive-900 leading-tight">
                      <Link to={`${productLinkBase}${p.slug}`}>{name}</Link>
                    </h2>
                    <p className="mt-3 text-sm text-olive-800/75 font-light leading-relaxed">{desc}</p>
                    {PRE_HARVEST_MODE ? (
                      /* Pre-harvest: no price, no cart, show waitlist */
                      <div className="mt-6 border-t border-cream-400 pt-4">
                        <div
                          data-testid={`shop-preharvest-note-${p.slug}`}
                          className="text-[10px] uppercase tracking-[0.22em] text-olive-800/55 mb-4"
                        >
                          {lang === "es"
                            ? "Próxima cosecha · aún no disponible"
                            : "Next harvest · not yet available"}
                        </div>
                        <button
                          onClick={() => openWaitlist(p.slug)}
                          data-testid={`shop-waitlist-${p.slug}`}
                          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.26em] hover:bg-olive-800 transition-colors"
                        >
                          <Bell size={14} strokeWidth={1.4} />
                          {lang === "es"
                            ? "Avísame cuando esté disponible"
                            : "Notify me when it is available"}
                        </button>
                      </div>
                    ) : (
                      /* Normal mode: show price + add to cart */
                      <>
                        <div className="mt-6 flex items-end justify-between border-t border-cream-400 pt-4">
                          <div>
                            <div className="font-serif text-3xl text-olive-900 leading-none">
                              {p.price.toFixed(2).replace(".", ",")}
                              <span className="text-sm text-olive-800/60 font-sans font-light ml-1">€</span>
                            </div>
                            <div
                              data-testid={`shop-vat-included-${p.slug}`}
                              className="mt-1 text-[10px] uppercase tracking-[0.22em] text-olive-800/55 font-light"
                            >
                              {t("product_page.vat_included")}
                            </div>
                            {savings && (
                              <div
                                data-testid={`shop-savings-${p.slug}`}
                                className="mt-2 text-xs font-light italic text-gold-deep"
                              >
                                {savings}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="mt-6 grid sm:grid-cols-2 gap-3">
                          <button
                            onClick={() => handleAdd(p)}
                            disabled={soldOut}
                            data-testid={`shop-add-${p.slug}`}
                            className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.26em] hover:bg-olive-800 disabled:bg-cream-400 disabled:text-olive-800/50 disabled:cursor-not-allowed transition-colors"
                          >
                            {added === p.id ? (
                              <>
                                <Check size={15} strokeWidth={1.5} />
                                {t("product_page.added")}
                              </>
                            ) : (
                              t("shop_page.add_to_cart")
                            )}
                          </button>
                          <Link
                            to={`${productLinkBase}${p.slug}`}
                            data-testid={`shop-view-${p.slug}`}
                            className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-olive-700/50 text-olive-800 text-[12px] uppercase tracking-[0.26em] hover:border-olive-700 hover:bg-cream-200 transition-colors"
                          >
                            {t("shop_page.view_product")}
                          </Link>
                        </div>
                      </>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <Footer />
      {showWaitlist && (
        <WaitlistForm
          onClose={() => setShowWaitlist(false)}
          preselectedProduct={waitlistProduct}
        />
      )}
    </main>
  );
}
